import {
  Box,
  Button,
  Center,
  keyframes,
  Link,
  Stack,
  Text,
  type UseDisclosureProps,
} from '@chakra-ui/react';
import { useEffect, useState, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseModal } from 'components';
import { ShopProductSku, type ShopProduct } from 'utils/graphql/hooks';

const animationDurationSeconds = 10;
const loadingBar = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

interface OrderConfirmationModalProps extends UseDisclosureProps {
  product: ShopProduct;
  paymentUrl?: string;
}

const RedirectModalContent: FunctionComponent<
  Required<Pick<OrderConfirmationModalProps, 'product' | 'paymentUrl'>>
> = ({ product, paymentUrl }) => {
  const { t } = useTranslation();
  const [counter, setCounter] = useState<number>(animationDurationSeconds);

  const isRedirecting = counter <= 0;

  useEffect(() => {
    const id = window.setInterval(() => {
      setCounter((count) => {
        if (count === 1) {
          window.clearInterval(id);
        }

        return count - 1;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    // if counter is 1 or less we are initiating the redirect, it takes most of the time at least
    // 2-3s until the redirect happens (related to WordPress)
    if (counter <= 1) {
      // replace works better than assign or simple setting of location.href
      // it does not conflict with the interval from first useEffect and the browser history is
      // almost the same since the checkout page redirects too if no sim cards are in the context
      window.location.replace(paymentUrl);
    }
  }, [counter, paymentUrl]);

  return (
    <Center as={Stack} spacing="2" pb="6">
      <Text pb="4">{t(getRedirectMessageI18nKey(product.sku))}</Text>
      <Text>
        {t(`shopCheckout.confirmation.${isRedirecting ? 'redirecting' : 'redirectionTime'}`, {
          ...(!isRedirecting ? { count: counter } : {}),
        })}
        {isRedirecting && '...'}
      </Text>
      <Box h="1" w="75%" overflow="hidden" border="1px solid">
        <Box
          h="full"
          bg="mGray.500"
          animation={`${loadingBar} ${animationDurationSeconds}s linear`}
        />
      </Box>
      <Link href={paymentUrl} variant="hoverBrand" pt="3" textDecoration="underline">
        {t('shopCheckout.confirmation.redirectNow')}
      </Link>
    </Center>
  );
};

export const OrderConfirmationModal: FunctionComponent<OrderConfirmationModalProps> = ({
  paymentUrl,
  product,
  isOpen = false,
  onClose = () => {},
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      header={t('shopCheckout.confirmation.title')}
      footer={
        paymentUrl ? undefined : (
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        )
      }
    >
      {paymentUrl ? (
        <RedirectModalContent paymentUrl={paymentUrl} product={product} />
      ) : (
        <Text>{t('shopCheckout.confirmation.bankTransferDataVolumeMessage')}</Text>
      )}
    </BaseModal>
  );
};

const getRedirectMessageI18nKey = (sku: ShopProductSku) => {
  switch (sku) {
    case ShopProductSku.Datavolume_1gb:
      return 'shopCheckout.confirmation.shopRedirectDataVolumeMessage';
    case ShopProductSku.WireguardBase_1m:
    case ShopProductSku.WireguardXl_1m:
      return 'shopCheckout.confirmation.shopRedirectWireGuardMessage';
    // those are not used/implemented yet:
    case ShopProductSku.SimClassic_01:
    case ShopProductSku.SimMff2_01:
    case ShopProductSku.SimEsim_01:
    default:
      return 'shopCheckout.confirmation.shopRedirectMessage';
  }
};
